<script setup>
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useOfferStore } from '../../stores/OfferStore';
  import { marked } from 'marked'
  import CartUnavailableOffers from './CartUnavailableOffers.vue';

  const { t } = useI18n();
  const store = useOfferStore();
  const { loading } = store;
  const props = defineProps({
    cart: {
      type: Object,
      required: true
    },
    cartOfferQuantity: {
      type: Number,
      required: false,
      default: 0
    }
  });

  const library = computed(() => {
    return props.cart.library;
  });

  const policy = computed(() => {
    return props.cart.policy;
  });

  const offersUnavailable = computed(() => {
    return store.offersUnavailable;
  });

  const onlyOfferDeleted = computed(() => {
    return store.onlyOfferDeleted;
  });

  const offersNotFromDeMarque = computed(() => {
    return store.offersNotFromDeMarque;
  });

  const lines = computed(() => {
    if (store.offers.length > 1) {
      return t('cart.lines', { lines: store.offers.length });
    }
    return t('cart.line', { lines: store.offers.length });
  });

  const copies = computed(() => {
    if (props.cartOfferQuantity > 1) {
      return t('cart.copies', { copies: props.cartOfferQuantity });
    }
    return t('cart.copy', { copies: props.cartOfferQuantity });
  });

  const cartStatusIcon = computed(() => {
    switch(props.cart.status) {
      case 'pending':
      case 'to_process':
      case 'in_processing':
        return 'bi-gear text-secondary';
      case 'failed':
        return 'bi-slash-circle text-danger';
      case 'validate':
        return 'bi-check-circle text-success'
      case 'new':
        return 'bi-star text-primary'
  }
  });

  const selectOffersUnavailable = (offers) => {
    const offerIds = offers.map(offer => offer.offer.id);
    const params = { id: props.cart.id, offerIds: offerIds };
    if(offerIds.length > 0) {

      if(!confirm(t('cart.header.confirm_delete'))) {
        return;
      }
      store.deleteCheckedOffersByCartId(params);
    }
    return false;
  };
</script>

<template>
  <div v-if="!loading">
    <div class="row">
      <div class="col text-center">
        <a class="me-2" :href="library.url" v-if="policy.display_library_data && library">{{ library.name }}</a>
        <span>
          {{ lines }},
        </span>
        <span>
          {{ copies }}
        </span>
        <span class="px-2 py-1 mx-2 mb-2 border rounded-2 bg-light border-secondary-subtle"><i :class="['bi', cartStatusIcon].join(' ')" aria-hidden="true"></i> {{ cart.will_validate_at ? t('cart.status.will_be_validated', { date: cart.will_validate_at }) :  t(`cart.status.${cart.status}`) }}</span>
        <span class="px-2 py-1 mx-0 mb-2 border rounded-2 bg-light border-secondary-subtle" v-if="cart.api_created"><i class="bi bi-robot" aria-hidden="true"></i> {{ t('cart.api_created') }}</span>
      </div>
    </div>
  </div>

  <CartUnavailableOffers
    :automaticOfferReplacementAndRemoval="cart.automatic_offer_replacement_and_removal"
    :offersUnavailable="offersUnavailable"
    :onlyOfferDeleted="onlyOfferDeleted"
    @selectOffersUnavailable="selectOffersUnavailable(offersUnavailable)">
      <template v-slot:header>
        <h2 class="cart-unavailable-offers-title fs-4">
          {{ t('cart.unavailable_offers.title') }}
        </h2>
        <p class="cart-unavailable-offers-content-details">
          <span class="cart-unavailable-offers-content">
            {{ t('cart.unavailable_offers.content') }}
          </span><br>
          {{ !cart.automatic_offer_replacement_and_removal ? t('cart.incompatible_offers.details') : t('cart.incompatible_offers.details_deleted') }}
        </p>
      </template>

      <template v-slot:footer>
        <p class="cart-unavailable-offers-footer">
          {{ t('cart.unavailable_offers.footer') }}
        </p>
      </template>
  </CartUnavailableOffers>

  <CartUnavailableOffers
    :automaticOfferReplacementAndRemoval="cart.automatic_offer_replacement_and_removal"
    :offersUnavailable="offersNotFromDeMarque"
    :onlyOfferDeleted="onlyOfferDeleted"
    @selectOffersUnavailable="selectOffersUnavailable(offersNotFromDeMarque)"
    v-if="cart.policy.customer_service">
      <template v-slot:header>
        <h2 class="cart-unavailable-offers-title fs-4">
          {{ t('cart.incompatible_offers.title') }}
        </h2>
        <p class="cart-unavailable-offers-content-details">
          <span class="cart-unavailable-offers-content">
            {{ t('cart.incompatible_offers.content') }}
          </span><br>
          {{ t('cart.incompatible_offers.details') }}
        </p>
      </template>
  </CartUnavailableOffers>

  <div class="alert alert-primary mt-3 mb-0 pb-0" role="status" v-if="cart.customer_service">
    <h2 class="fs-4">{{ t('offers.carts.customer_service_title') }}</h2>
    <p v-html="t('offers.carts.customer_service_text_html', { br: '<br />' })"></p>
  </div>

  <div class="alert alert-warning mt-3 mb-0 pb-0" role="status" v-if="cart.suggested_cart">
    <h2 class="fs-4">{{ t('offers.carts.disclaimer_1') }} <a href="https://www.recommandation.ca/" class="alert-link">recommandation.ca</a>.</h2>
    <p v-html="marked(t('offers.carts.disclaimer_2'))"></p>
  </div>
</template>

<style scoped>
  .no-bottom-margin {
    margin-bottom: 0;
  }
  .cart-disclaimer {
    margin-bottom: 30px;
  }
</style>
